import React, { useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Skeleton,
    Collapse,
    LinearProgress,
    FormControlLabel,
    Switch,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { ReactComponent as PushPinSvg } from '../../../assets/images/PushPin.svg'
import SvgIcon from '@mui/material/SvgIcon'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import LockIcon from '@mui/icons-material/Lock'
import AdminIcon from '@mui/icons-material/SupervisorAccount'
import {
    useAdminOrganizationsQuery,
    useUpdateOrganizationFlagsMutation,
    AdminOrganization,
    useAdminUsersByOrgQuery,
    AdminUser,
    useAdminRoadmapsByOrgQuery,
    AdminRoadmap,
    useAdminRoadmapStatsQuery,
    RoadmapStats,
    useAdminRoadmapChangesQuery,
    RoadmapChange,
} from '../../../shared/queries/admin'

// ─── Types ────────────────────────────────────────────────────────────────────

type ExpandedType = 'users' | 'roadmaps' | null
type SortField = 'name' | 'userCount' | 'roadmapCount' | 'createdAt'
type SortDirection = 'asc' | 'desc'

// ─── Module-level helpers ──────────────────────────────────────────────────────

const INTERNAL_EMAIL_DOMAINS = ['@aesara.com', '@arysana.com', '@arysana.io']

const isInternalEmail = (email: string): boolean => {
    const lowerEmail = email.toLowerCase()
    return INTERNAL_EMAIL_DOMAINS.some((domain) => lowerEmail.endsWith(domain))
}

const isInternalOrg = (orgName: string): boolean => {
    const lowerName = orgName.toLowerCase()
    return lowerName.includes('aesara') || lowerName.includes('arysana') || lowerName.includes('demo')
}

// ─── OrgUsersExpandableRow ────────────────────────────────────────────────────

interface OrgUsersExpandableRowProps {
    orgId: string
    orgName: string
    isExpanded: boolean
}

const OrgUsersExpandableRow: React.FC<OrgUsersExpandableRowProps> = ({ orgId, orgName, isExpanded }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [showInternalUsers, setShowInternalUsers] = useState(() => isInternalOrg(orgName))

    const { data, isLoading } = useAdminUsersByOrgQuery(isExpanded ? orgId : '', 1, 100, searchTerm)

    const allUsers = data?.data?.users || []

    const users = useMemo(() => {
        if (showInternalUsers) return allUsers
        return allUsers.filter((user) => !isInternalEmail(user.email))
    }, [allUsers, showInternalUsers])

    const internalCount = allUsers.filter((user) => isInternalEmail(user.email)).length

    const formatDate = (dateString: string) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return isNaN(date.getTime()) ? '' : date.toLocaleDateString()
    }

    const formatLastActive = (dateString: string | null) => {
        if (!dateString) return 'Never'
        const date = new Date(dateString)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffMins = Math.floor(diffMs / (1000 * 60))
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        if (diffMins < 60) return `${diffMins}m ago`
        if (diffHours < 24) return `${diffHours}h ago`
        if (diffDays === 1) return 'Yesterday'
        if (diffDays < 7) return `${diffDays}d ago`
        if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
        return date.toLocaleDateString()
    }

    const getActivityColor = (lastActivity: string | null) => {
        if (!lastActivity) return 'figma.grayscale.stroke'
        const now = new Date()
        const lastDate = new Date(lastActivity)
        const daysSince = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24))

        if (daysSince <= 1) return 'figma.accentColors.orange'
        if (daysSince <= 7) return 'figma.accentColors.yellow'
        if (daysSince <= 30) return 'figma.accentColors.teal'
        return 'figma.primary2'
    }

    return (
        <TableRow>
            <TableCell colSpan={7} padding="none" sx={{ border: 'none' }}>
                {isLoading && isExpanded && <LinearProgress />}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ py: 2, px: 3, pl: 7, bgcolor: 'figma.grayscale.table1' }}>
                        {/* Search and Filter Controls */}
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <TextField
                                size="small"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: 250 }}
                            />
                            {internalCount > 0 && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={showInternalUsers}
                                            onChange={(e) => setShowInternalUsers(e.target.checked)}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" color="figma.grayscale.textHier2">
                                            Show internal users ({internalCount})
                                        </Typography>
                                    }
                                />
                            )}
                        </Box>

                        {/* Users Table */}
                        {users.length === 0 ? (
                            <Typography variant="body2" color="figma.grayscale.textHier2">
                                {searchTerm ? 'No users match your search' : 'No users found'}
                            </Typography>
                        ) : (
                            <Table size="small" sx={{ bgcolor: 'figma.grayscale.contentWrapper', borderRadius: 1 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }} align="center">
                                            Role
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>Last Active</TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>Joined</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user: AdminUser) => (
                                        <TableRow key={user._id} hover>
                                            <TableCell sx={{ py: 0.75 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2">{user.name}</Typography>
                                                    {isInternalEmail(user.email) && (
                                                        <Chip
                                                            label="Internal"
                                                            size="small"
                                                            sx={{ height: 18, fontSize: 10 }}
                                                            color="info"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 0.75 }}>
                                                <Typography variant="body2" color="figma.grayscale.textHier2">
                                                    {user.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 0.75 }} align="center">
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                    {user.isAdmin && (
                                                        <Tooltip title="Admin">
                                                            <AdminIcon
                                                                fontSize="small"
                                                                color="primary"
                                                                sx={{ fontSize: 18 }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    {user.readonly && (
                                                        <Chip
                                                            label="RO"
                                                            size="small"
                                                            sx={{ height: 18, fontSize: 10 }}
                                                        />
                                                    )}
                                                    {!user.isAdmin && !user.readonly && (
                                                        <Typography variant="caption" color="figma.grayscale.textHier2">
                                                            —
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 0.75 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            bgcolor: getActivityColor(user.lastActivity),
                                                        }}
                                                    />
                                                    <Tooltip
                                                        title={
                                                            user.lastActivity
                                                                ? new Date(user.lastActivity).toLocaleString(
                                                                      undefined,
                                                                      { timeZoneName: 'short' }
                                                                  )
                                                                : ''
                                                        }
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color={
                                                                user.lastActivity ? 'text.primary' : 'text.secondary'
                                                            }
                                                        >
                                                            {formatLastActive(user.lastActivity)}
                                                        </Typography>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 0.75 }}>
                                                <Typography variant="body2" color="figma.grayscale.textHier2">
                                                    {formatDate(user.createdAt)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        {allUsers.length > 0 && (
                            <Typography
                                variant="caption"
                                color="figma.grayscale.textHier2"
                                sx={{ mt: 1, display: 'block' }}
                            >
                                Showing {users.length} of {allUsers.length} users
                                {!showInternalUsers && internalCount > 0 && ` (${internalCount} internal hidden)`}
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    )
}

// ─── ActivityHeatmap ──────────────────────────────────────────────────────────

interface ActivityHeatmapProps {
    activityByWeek: Record<string, number>
}

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({ activityByWeek }) => {
    const weeks = useMemo(() => {
        const result: string[] = []
        const now = new Date()
        const dayOfWeek = now.getDay()
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
        const currentMonday = new Date(now)
        currentMonday.setDate(now.getDate() + mondayOffset)
        currentMonday.setHours(0, 0, 0, 0)

        for (let i = 11; i >= 0; i--) {
            const monday = new Date(currentMonday)
            monday.setDate(currentMonday.getDate() - i * 7)
            const year = monday.getFullYear()
            const month = String(monday.getMonth() + 1).padStart(2, '0')
            const day = String(monday.getDate()).padStart(2, '0')
            result.push(`${year}-${month}-${day}`)
        }
        return result
    }, [])

    const counts = weeks.map((week) => activityByWeek[week] || 0)
    const maxActivity = Math.max(...counts, 1)

    return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {weeks.map((week, idx) => {
                const count = counts[idx]
                const intensity = count / maxActivity
                const bgColor =
                    count === 0
                        ? 'figma.grayscale.stroke'
                        : intensity < 0.33
                          ? 'success.light'
                          : intensity < 0.66
                            ? 'success.main'
                            : 'success.dark'

                const weekDate = new Date(week)
                const weekLabel = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

                return (
                    <Tooltip key={week} title={`${weekLabel}: ${count} changes`}>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: 0.5,
                                bgcolor: bgColor,
                                opacity: count === 0 ? 0.4 : 1,
                            }}
                        />
                    </Tooltip>
                )
            })}
            <Typography variant="caption" color="figma.grayscale.textHier2" sx={{ ml: 0.5 }}>
                12w
            </Typography>
        </Box>
    )
}

// ─── RoadmapRowWithStats ──────────────────────────────────────────────────────

interface RoadmapRowWithStatsProps {
    roadmap: AdminRoadmap
}

const RoadmapRowWithStats: React.FC<RoadmapRowWithStatsProps> = ({ roadmap }) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const { data, isLoading } = useAdminRoadmapStatsQuery(roadmap._id, true)
    const { data: changesData, isLoading: changesLoading } = useAdminRoadmapChangesQuery(roadmap._id, isExpanded, 30)
    const stats = data?.data
    const changes = changesData?.data?.changes || []

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '—'
        const date = new Date(dateString)
        return isNaN(date.getTime()) ? '—' : date.toLocaleDateString()
    }

    const formatTimestamp = (dateString: string) => {
        const date = new Date(dateString)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffMins = Math.floor(diffMs / (1000 * 60))
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        if (diffMins < 60) return `${diffMins}m ago`
        if (diffHours < 24) return `${diffHours}h ago`
        if (diffDays === 1) return 'Yesterday'
        if (diffDays < 7) return `${diffDays}d ago`
        return date.toLocaleDateString()
    }

    return (
        <>
            <TableRow
                hover
                sx={{ cursor: 'pointer', bgcolor: isExpanded ? 'figma.primary3' : 'inherit' }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <TableCell sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" sx={{ p: 0.25 }}>
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                        <Typography variant="body2" fontWeight={500}>
                            {roadmap.product}
                        </Typography>
                        {!roadmap.isActive && (
                            <Chip label="Inactive" size="small" color="default" sx={{ height: 18, fontSize: 10 }} />
                        )}
                    </Box>
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" color="figma.grayscale.textHier2">
                        {roadmap.indication}
                    </Typography>
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                    {isLoading ? (
                        <Skeleton width={120} />
                    ) : stats ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Value Messages">
                                <Chip
                                    label={`VM:${stats.vmCount}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 10 }}
                                />
                            </Tooltip>
                            <Tooltip title="Evidence">
                                <Chip
                                    label={`Ev:${stats.evidenceCount}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 10 }}
                                />
                            </Tooltip>
                            <Tooltip title="Activities">
                                <Chip
                                    label={`Act:${stats.activityCount}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 10 }}
                                />
                            </Tooltip>
                            <Tooltip title="Deliverables">
                                <Chip
                                    label={`Del:${stats.deliverableCount}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 10 }}
                                />
                            </Tooltip>
                        </Box>
                    ) : (
                        <Typography variant="caption" color="figma.grayscale.textHier2">
                            —
                        </Typography>
                    )}
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                    {isLoading ? (
                        <Skeleton width={160} />
                    ) : stats ? (
                        <ActivityHeatmap activityByWeek={stats.activityByWeek} />
                    ) : (
                        <Typography variant="caption" color="figma.grayscale.textHier2">
                            —
                        </Typography>
                    )}
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                    {isLoading ? (
                        <Skeleton width={80} />
                    ) : (
                        <Typography variant="body2" color="figma.grayscale.textHier2">
                            {formatDate(stats?.lastModified || null)}
                        </Typography>
                    )}
                </TableCell>
            </TableRow>
            {/* Expandable change log row */}
            <TableRow>
                <TableCell colSpan={5} padding="none" sx={{ border: 'none' }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1.5, px: 2, pl: 5, bgcolor: 'figma.grayscale.table2' }}>
                            <Typography
                                variant="caption"
                                fontWeight={600}
                                color="figma.grayscale.textHier2"
                                sx={{ display: 'block', mb: 1 }}
                            >
                                Recent Changes
                            </Typography>
                            {changesLoading ? (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Skeleton width={200} />
                                    <Skeleton width={200} />
                                </Box>
                            ) : changes.length === 0 ? (
                                <Typography variant="caption" color="figma.grayscale.textHier2">
                                    No recent changes
                                </Typography>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        maxHeight: 200,
                                        overflow: 'auto',
                                    }}
                                >
                                    {changes.map((change: RoadmapChange) => (
                                        <Box
                                            key={change._id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                py: 0.5,
                                                borderBottom: '1px solid',
                                                borderColor: 'figma.grayscale.stroke',
                                            }}
                                        >
                                            <Chip
                                                label={change.typeShort}
                                                size="small"
                                                color={
                                                    change.typeShort === 'VM'
                                                        ? 'primary'
                                                        : change.typeShort === 'Ev'
                                                          ? 'success'
                                                          : change.typeShort === 'Act'
                                                            ? 'warning'
                                                            : 'default'
                                                }
                                                sx={{ height: 18, fontSize: 10, minWidth: 36 }}
                                            />
                                            <Chip
                                                label={change.action}
                                                size="small"
                                                variant="outlined"
                                                color={
                                                    change.action === 'create'
                                                        ? 'success'
                                                        : change.action === 'delete'
                                                          ? 'error'
                                                          : 'info'
                                                }
                                                sx={{ height: 18, fontSize: 10 }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    flex: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: 300,
                                                }}
                                            >
                                                {change.title || '(untitled)'}
                                            </Typography>
                                            <Tooltip
                                                title={
                                                    change.timestamp
                                                        ? new Date(change.timestamp).toLocaleString(undefined, {
                                                              timeZoneName: 'short',
                                                          })
                                                        : ''
                                                }
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="figma.grayscale.textHier2"
                                                    sx={{ whiteSpace: 'nowrap' }}
                                                >
                                                    {formatTimestamp(change.timestamp)}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

// ─── OrgRoadmapsExpandableRow ─────────────────────────────────────────────────

interface OrgRoadmapsExpandableRowProps {
    orgId: string
    isExpanded: boolean
}

const OrgRoadmapsExpandableRow: React.FC<OrgRoadmapsExpandableRowProps> = ({ orgId, isExpanded }) => {
    const { data, isLoading } = useAdminRoadmapsByOrgQuery(isExpanded ? orgId : '', 1, 50)
    const roadmaps = data?.data?.roadmaps || []

    return (
        <TableRow>
            <TableCell colSpan={7} padding="none" sx={{ border: 'none' }}>
                {isLoading && isExpanded && <LinearProgress />}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ py: 2, px: 3, pl: 7, bgcolor: 'figma.grayscale.table1' }}>
                        {roadmaps.length === 0 ? (
                            <Typography variant="body2" color="figma.grayscale.textHier2">
                                No roadmaps found
                            </Typography>
                        ) : (
                            <Table size="small" sx={{ bgcolor: 'figma.grayscale.contentWrapper', borderRadius: 1 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>Product</TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>Indication</TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>Content</TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>Activity (12 weeks)</TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>Last Modified</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {roadmaps.map((roadmap: AdminRoadmap) => (
                                        <RoadmapRowWithStats key={roadmap._id} roadmap={roadmap} />
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        {roadmaps.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="caption" color="figma.grayscale.textHier2">
                                    Showing {roadmaps.length} roadmaps
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            bgcolor: 'figma.grayscale.stroke',
                                            borderRadius: 0.5,
                                            opacity: 0.4,
                                        }}
                                    />
                                    <Typography variant="caption" color="figma.grayscale.textHier2">
                                        No activity
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            bgcolor: 'success.light',
                                            borderRadius: 0.5,
                                            ml: 1,
                                        }}
                                    />
                                    <Typography variant="caption" color="figma.grayscale.textHier2">
                                        Low
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            bgcolor: 'success.main',
                                            borderRadius: 0.5,
                                            ml: 1,
                                        }}
                                    />
                                    <Typography variant="caption" color="figma.grayscale.textHier2">
                                        Medium
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            bgcolor: 'success.dark',
                                            borderRadius: 0.5,
                                            ml: 1,
                                        }}
                                    />
                                    <Typography variant="caption" color="figma.grayscale.textHier2">
                                        High
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    )
}

// ─── OrganizationsTab ─────────────────────────────────────────────────────────

const OrganizationsTab: React.FC = () => {
    const history = useHistory()
    const [searchTerm, setSearchTerm] = useState('')
    const [sortField, setSortField] = useState<SortField>('name')
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    })
    const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null)
    const [expandedType, setExpandedType] = useState<ExpandedType>(null)

    const { data, isLoading, error } = useAdminOrganizationsQuery(1, 200)
    const updateFlagsMutation = useUpdateOrganizationFlagsMutation()

    const organizations = data?.data?.organizations || []

    const sortedOrgs = useMemo(() => {
        let filtered = searchTerm
            ? organizations.filter((org) => org.name.toLowerCase().includes(searchTerm.toLowerCase()))
            : organizations

        return [...filtered].sort((a, b) => {
            const aIsFavorite = a.isFavorite || false
            const bIsFavorite = b.isFavorite || false
            if (aIsFavorite && !bIsFavorite) return -1
            if (!aIsFavorite && bIsFavorite) return 1

            let comparison = 0
            switch (sortField) {
                case 'name':
                    comparison = a.name.localeCompare(b.name)
                    break
                case 'userCount':
                    comparison = a.userCount - b.userCount
                    break
                case 'roadmapCount':
                    comparison = a.roadmapCount - b.roadmapCount
                    break
                case 'createdAt':
                    comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                    break
            }
            return sortDirection === 'asc' ? comparison : -comparison
        })
    }, [organizations, searchTerm, sortField, sortDirection])

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    const handleTogglePin = async (e: React.MouseEvent, org: AdminOrganization) => {
        e.stopPropagation()
        try {
            await updateFlagsMutation.mutateAsync({
                orgId: org._id,
                flags: { isFavorite: !org.isFavorite },
            })
            setSnackbar({ open: true, message: org.isFavorite ? 'Unpinned' : 'Pinned', severity: 'success' })
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to update', severity: 'error' })
        }
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return isNaN(date.getTime()) ? '' : date.toLocaleDateString()
    }

    const handleToggleExpand = (e: React.MouseEvent, orgId: string, type: ExpandedType) => {
        e.stopPropagation()
        if (expandedOrgId === orgId && expandedType === type) {
            setExpandedOrgId(null)
            setExpandedType(null)
        } else {
            setExpandedOrgId(orgId)
            setExpandedType(type)
        }
    }

    const pinnedCount = organizations.filter((org) => org.isFavorite).length

    return (
        <>
            {/* Search and Stats */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="Search organizations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 280 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {pinnedCount > 0 && (
                        <Chip
                            icon={<SvgIcon component={PushPinSvg} inheritViewBox sx={{ fontSize: 16 }} />}
                            label={`${pinnedCount} pinned`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load organizations
                </Alert>
            )}

            {/* Table */}
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    maxHeight: 'calc(100vh - 280px)',
                    border: '1px solid',
                    borderColor: 'figma.grayscale.stroke',
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead sx={{ bgcolor: 'figma.grayscale.table2' }}>
                        <TableRow>
                            <TableCell
                                padding="checkbox"
                                sx={{ width: 40, bgcolor: 'figma.grayscale.table2' }}
                            ></TableCell>
                            <TableCell
                                sx={{
                                    bgcolor: 'figma.grayscale.table2',
                                    color: 'figma.grayscale.textHier2',
                                    fontWeight: 600,
                                    fontSize: 12,
                                }}
                            >
                                <TableSortLabel
                                    active={sortField === 'name'}
                                    direction={sortField === 'name' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('name')}
                                >
                                    Organization
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    bgcolor: 'figma.grayscale.table2',
                                    color: 'figma.grayscale.textHier2',
                                    fontWeight: 600,
                                    fontSize: 12,
                                }}
                            >
                                <TableSortLabel
                                    active={sortField === 'userCount'}
                                    direction={sortField === 'userCount' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('userCount')}
                                >
                                    Users
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    bgcolor: 'figma.grayscale.table2',
                                    color: 'figma.grayscale.textHier2',
                                    fontWeight: 600,
                                    fontSize: 12,
                                }}
                            >
                                <TableSortLabel
                                    active={sortField === 'roadmapCount'}
                                    direction={sortField === 'roadmapCount' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('roadmapCount')}
                                >
                                    Roadmaps
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    bgcolor: 'figma.grayscale.table2',
                                    color: 'figma.grayscale.textHier2',
                                    fontWeight: 600,
                                    fontSize: 12,
                                }}
                            >
                                Features
                            </TableCell>
                            <TableCell
                                sx={{
                                    bgcolor: 'figma.grayscale.table2',
                                    color: 'figma.grayscale.textHier2',
                                    fontWeight: 600,
                                    fontSize: 12,
                                }}
                            >
                                <TableSortLabel
                                    active={sortField === 'createdAt'}
                                    direction={sortField === 'createdAt' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('createdAt')}
                                >
                                    Created
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    width: 60,
                                    bgcolor: 'figma.grayscale.table2',
                                    color: 'figma.grayscale.textHier2',
                                    fontWeight: 600,
                                    fontSize: 12,
                                }}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading
                            ? Array.from({ length: 10 }).map((_, i) => (
                                  <TableRow key={i}>
                                      <TableCell>
                                          <Skeleton width={24} />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton width={40} />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton width={40} />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton width={120} />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton width={80} />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton width={30} />
                                      </TableCell>
                                  </TableRow>
                              ))
                            : sortedOrgs.map((org) => {
                                  const isUsersExpanded = expandedOrgId === org._id && expandedType === 'users'
                                  const isRoadmapsExpanded = expandedOrgId === org._id && expandedType === 'roadmaps'
                                  return (
                                      <React.Fragment key={org._id}>
                                          <TableRow
                                              hover
                                              sx={{
                                                  cursor: 'pointer',
                                                  bgcolor: org.isFavorite ? 'figma.primary3' : 'inherit',
                                                  '&:hover': { bgcolor: 'figma.grayscale.frameBackground' },
                                              }}
                                              onClick={() => history.push(`/admin/organizations/${org._id}`)}
                                          >
                                              <TableCell padding="checkbox">
                                                  <Tooltip title={org.isFavorite ? 'Unpin' : 'Pin'}>
                                                      <IconButton
                                                          size="small"
                                                          disableRipple
                                                          onClick={(e) => handleTogglePin(e, org)}
                                                          sx={{
                                                              color: org.isFavorite
                                                                  ? 'figma.primary'
                                                                  : 'action.disabled',
                                                          }}
                                                      >
                                                          <SvgIcon
                                                              component={PushPinSvg}
                                                              inheritViewBox
                                                              sx={{ fontSize: 18, opacity: org.isFavorite ? 1 : 0.4 }}
                                                          />
                                                      </IconButton>
                                                  </Tooltip>
                                              </TableCell>
                                              <TableCell>
                                                  <Typography variant="body2" fontWeight={org.isFavorite ? 600 : 400}>
                                                      {org.name}
                                                  </Typography>
                                              </TableCell>
                                              <TableCell align="center">
                                                  <Tooltip title={isUsersExpanded ? 'Collapse' : 'View users'}>
                                                      <Chip
                                                          label={org.userCount}
                                                          size="small"
                                                          variant={isUsersExpanded ? 'filled' : 'outlined'}
                                                          color={isUsersExpanded ? 'primary' : 'default'}
                                                          onClick={(e) => handleToggleExpand(e, org._id, 'users')}
                                                          icon={
                                                              isUsersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                                                          }
                                                          sx={{ cursor: 'pointer' }}
                                                      />
                                                  </Tooltip>
                                              </TableCell>
                                              <TableCell align="center">
                                                  <Tooltip title={isRoadmapsExpanded ? 'Collapse' : 'View roadmaps'}>
                                                      <Chip
                                                          label={org.roadmapCount}
                                                          size="small"
                                                          variant={isRoadmapsExpanded ? 'filled' : 'outlined'}
                                                          color={isRoadmapsExpanded ? 'primary' : 'default'}
                                                          onClick={(e) => handleToggleExpand(e, org._id, 'roadmaps')}
                                                          icon={
                                                              isRoadmapsExpanded ? (
                                                                  <ExpandLessIcon />
                                                              ) : (
                                                                  <ExpandMoreIcon />
                                                              )
                                                          }
                                                          sx={{ cursor: 'pointer' }}
                                                      />
                                                  </Tooltip>
                                              </TableCell>
                                              <TableCell align="center">
                                                  <Box
                                                      sx={{
                                                          display: 'flex',
                                                          gap: 0.5,
                                                          justifyContent: 'center',
                                                          flexWrap: 'wrap',
                                                      }}
                                                  >
                                                      {org.isPicoEnabled && (
                                                          <Chip
                                                              label="PICO"
                                                              size="small"
                                                              color="primary"
                                                              sx={{ height: 20, fontSize: 11 }}
                                                          />
                                                      )}
                                                      {org.isMaiaEnabled && (
                                                          <Chip
                                                              label="MAIA"
                                                              size="small"
                                                              color="secondary"
                                                              sx={{ height: 20, fontSize: 11 }}
                                                          />
                                                      )}
                                                      {org.isFreeTrial && (
                                                          <Chip
                                                              label="Trial"
                                                              size="small"
                                                              color="warning"
                                                              sx={{ height: 20, fontSize: 11 }}
                                                          />
                                                      )}
                                                      {org.isAllowedUsersEnabled && (
                                                          <Tooltip title="Roadmap access restricted">
                                                              <LockIcon
                                                                  sx={{
                                                                      fontSize: 16,
                                                                      color: 'figma.grayscale.textHier2',
                                                                  }}
                                                              />
                                                          </Tooltip>
                                                      )}
                                                      {!org.isPicoEnabled &&
                                                          !org.isMaiaEnabled &&
                                                          !org.isFreeTrial &&
                                                          !org.isAllowedUsersEnabled && (
                                                              <Typography
                                                                  variant="caption"
                                                                  color="figma.grayscale.textHier2"
                                                              >
                                                                  —
                                                              </Typography>
                                                          )}
                                                  </Box>
                                              </TableCell>
                                              <TableCell>
                                                  <Typography variant="body2" color="figma.grayscale.textHier2">
                                                      {formatDate(org.createdAt)}
                                                  </Typography>
                                              </TableCell>
                                              <TableCell align="center">
                                                  <Tooltip title="View Details">
                                                      <IconButton
                                                          size="small"
                                                          onClick={(e) => {
                                                              e.stopPropagation()
                                                              history.push(`/admin/organizations/${org._id}`)
                                                          }}
                                                      >
                                                          <OpenInNewIcon fontSize="small" />
                                                      </IconButton>
                                                  </Tooltip>
                                              </TableCell>
                                          </TableRow>
                                          <OrgUsersExpandableRow
                                              orgId={org._id}
                                              orgName={org.name}
                                              isExpanded={isUsersExpanded}
                                          />
                                          <OrgRoadmapsExpandableRow orgId={org._id} isExpanded={isRoadmapsExpanded} />
                                      </React.Fragment>
                                  )
                              })}
                    </TableBody>
                </Table>
            </TableContainer>

            {!isLoading && sortedOrgs.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="figma.grayscale.textHier2">
                        {searchTerm ? 'No organizations match your search' : 'No organizations found'}
                    </Typography>
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={2000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    )
}

export default OrganizationsTab
